import {
  hasLightLineOfSight,
  isDeskLightOn,
  isOfficeNight,
  type MapData,
  type PointLightDef,
  type Rect,
  type Vec2,
  type WindowLightDef,
  windowLightOrigin,
} from "@agency/shared";
import Phaser from "phaser";
import { store } from "../../store/GameStore.js";

const AMBIENT = "#171c2b";
// Above world tiles and below overhead labels and the bathroom ceiling.
const LIGHT_DEPTH = 100_001;
const TEXTURE_KEY = "office-light-field";
const GLOW_KEY = "office-light-glow";
const CEILING_KEY = "office-ceiling-shade";

const LIGHT_STYLE = {
  desk: { radius: 44, color: "235,190,120", strength: 0.9 },
  screen: { radius: 30, color: "110,185,245", strength: 0.55 },
  lamp: { radius: 100, color: "240,195,125", strength: 0.95 },
} as const;

interface LightStamp {
  canvas: HTMLCanvasElement;
  x: number;
  y: number;
}

/** A world-space light field, shared by local and remote desk occupants. */
export class OfficeLighting {
  private readonly texture: Phaser.Textures.CanvasTexture;
  private readonly field: Phaser.GameObjects.Image;
  private readonly glows: Phaser.GameObjects.Image[] = [];
  private readonly ceilingShade?: Phaser.GameObjects.Image;
  private readonly pointStamps: LightStamp[];
  private readonly windowStamps: LightStamp[];
  private signature = "";
  private night?: boolean;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly map: MapData,
    private readonly ceiling?: Phaser.Tilemaps.TilemapLayer | Phaser.Tilemaps.TilemapGPULayer,
  ) {
    // The map and sources are static. Resolve wall shadows once, then reuse
    // the masked light images whenever synchronized occupancy changes.
    this.pointStamps = map.pointLights.map((light) => {
      const radius = LIGHT_STYLE[light.kind].radius;
      return this.createStamp(
        { x: light.x - radius, y: light.y - radius, w: radius * 2, h: radius * 2 },
        (ctx) => this.drawPoint(ctx, light),
        () => light,
      );
    });
    this.windowStamps = map.windowLights.map((window) => {
      const { x, y, w, h } = window.rect;
      const reach = this.windowReach(window);
      return this.createStamp(
        { x: x - 16, y: window.direction === "down" ? y : y - reach, w: w + 32, h: reach + h },
        (ctx) => this.drawWindow(ctx, window),
        (target) => windowLightOrigin(map, window, target.x),
      );
    });
    const texture = scene.textures.createCanvas(
      TEXTURE_KEY,
      map.width * map.tileSize,
      map.height * map.tileSize,
    );
    if (!texture) throw new Error("Could not create office lighting texture");
    this.texture = texture;
    this.field = scene.add
      .image(0, 0, TEXTURE_KEY)
      .setOrigin(0)
      .setDepth(LIGHT_DEPTH)
      .setBlendMode(Phaser.BlendModes.MULTIPLY)
      .setVisible(false);

    if (ceiling) {
      // Canvas tile layers do not support tint. A separate mask darkens the
      // concealed roof in either renderer and follows its reveal fade.
      const shade = scene.textures.createCanvas(CEILING_KEY, texture.width, texture.height);
      if (!shade) throw new Error("Could not create office ceiling shade texture");
      shade.context.fillStyle = AMBIENT;
      for (const row of ceiling.layer.data) {
        for (const tile of row) {
          if (tile && tile.index >= 0) {
            shade.context.fillRect(tile.pixelX, tile.pixelY, tile.width, tile.height);
          }
        }
      }
      shade.refresh();
      this.ceilingShade = scene.add
        .image(ceiling.x, ceiling.y, CEILING_KEY)
        .setOrigin(0)
        .setDepth(ceiling.depth + 1)
        .setBlendMode(Phaser.BlendModes.MULTIPLY)
        .setVisible(false);
    }

    const glow = scene.textures.createCanvas(GLOW_KEY, 48, 16);
    if (!glow) throw new Error("Could not create office lamp glow texture");
    const ctx = glow.context;
    Object.entries(LIGHT_STYLE).forEach(([kind, style], index) => {
      const x = index * 16;
      const gradient = ctx.createRadialGradient(x + 8, 8, 0, x + 8, 8, 8);
      gradient.addColorStop(0, `rgba(${style.color},0.8)`);
      gradient.addColorStop(0.2, `rgba(${style.color},0.35)`);
      gradient.addColorStop(1, `rgba(${style.color},0)`);
      ctx.fillStyle = gradient;
      ctx.fillRect(x, 0, 16, 16);
      glow.add(kind, 0, x, 0, 16, 16);
    });
    glow.refresh();
    for (const light of map.pointLights) {
      this.glows.push(
        scene.add
          .image(light.x, light.y, GLOW_KEY, light.kind)
          .setDepth(LIGHT_DEPTH + 1)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setScale(light.kind === "screen" ? 0.7 : 1, light.kind === "screen" ? 0.35 : 1)
          .setVisible(false),
      );
    }
    this.update();
  }

  update(): void {
    const night = isOfficeNight(Date.now() + store.serverOffset);
    if (night !== this.night) {
      this.night = night;
      this.field.setVisible(night);
      this.ceilingShade?.setVisible(night);
    }
    this.ceilingShade?.setAlpha(this.ceiling?.alpha ?? 0);
    const active = this.map.pointLights.map(
      (light) =>
        night &&
        (light.kind === "lamp" || isDeskLightOn(light.deskId!, store.desks, store.players)),
    );
    const signature = `${night}:${active.map(Number).join("")}`;
    if (signature === this.signature) return;
    this.signature = signature;
    this.glows.forEach((glow, i) => {
      glow.setVisible(active[i]!);
    });
    if (!night) return;

    // Upload only on a day/night or occupancy change, never every frame.
    const ctx = this.texture.context;
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = AMBIENT;
    ctx.fillRect(0, 0, this.texture.width, this.texture.height);
    ctx.globalCompositeOperation = "lighter";
    for (const stamp of this.windowStamps) ctx.drawImage(stamp.canvas, stamp.x, stamp.y);
    this.pointStamps.forEach((stamp, i) => {
      if (active[i]) ctx.drawImage(stamp.canvas, stamp.x, stamp.y);
    });
    ctx.globalCompositeOperation = "source-over";
    this.texture.refresh();
  }

  private createStamp(
    bounds: Rect,
    draw: (ctx: CanvasRenderingContext2D) => void,
    origin: (target: Vec2) => Vec2,
  ): LightStamp {
    const x = Math.floor(bounds.x);
    const y = Math.floor(bounds.y);
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(bounds.x + bounds.w) - x;
    canvas.height = Math.ceil(bounds.y + bounds.h) - y;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not create office light shadow canvas");
    ctx.translate(-x, -y);
    draw(ctx);
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let row = 0; row < canvas.height; row++) {
      for (let col = 0; col < canvas.width; col++) {
        const alpha = (row * canvas.width + col) * 4 + 3;
        if (image.data[alpha] === 0) continue;
        const target = { x: x + col + 0.5, y: y + row + 0.5 };
        if (!hasLightLineOfSight(this.map, origin(target), target)) image.data[alpha] = 0;
      }
    }
    ctx.putImageData(image, 0, 0);
    return { canvas, x, y };
  }

  private windowReach(window: WindowLightDef): number {
    return window.rect.w < 48 ? 64 : 96;
  }

  private drawPoint(ctx: CanvasRenderingContext2D, light: PointLightDef): void {
    const { radius, color, strength } = LIGHT_STYLE[light.kind];
    const gradient = ctx.createRadialGradient(light.x, light.y, 0, light.x, light.y, radius);
    gradient.addColorStop(0, `rgba(${color},${strength})`);
    gradient.addColorStop(0.2, `rgba(${color},${strength * 0.85})`);
    gradient.addColorStop(0.55, `rgba(${color},${strength * 0.3})`);
    gradient.addColorStop(1, `rgba(${color},0)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(light.x - radius, light.y - radius, radius * 2, radius * 2);
  }

  private drawWindow(ctx: CanvasRenderingContext2D, window: WindowLightDef): void {
    const { x, y, w, h } = window.rect;
    const down = window.direction === "down";
    const reach = this.windowReach(window);
    ctx.save();
    // Keep all spill on the inward side indicated by the Tiled class.
    ctx.beginPath();
    ctx.rect(x - 16, down ? y : y - reach, w + 32, reach + h);
    ctx.clip();
    ctx.translate(x + w / 2, down ? y + h : y);
    ctx.scale(w / 2 + 16, reach);
    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    gradient.addColorStop(0, "rgba(105,145,210,0.4)");
    gradient.addColorStop(0.3, "rgba(105,145,210,0.25)");
    gradient.addColorStop(1, "rgba(105,145,210,0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(-1, -1, 2, 2);
    ctx.restore();
  }

  destroy(): void {
    this.field.destroy();
    this.ceilingShade?.destroy();
    for (const glow of this.glows) glow.destroy();
    for (const stamp of [...this.pointStamps, ...this.windowStamps]) {
      stamp.canvas.width = 0;
      stamp.canvas.height = 0;
    }
    this.scene.textures.remove(TEXTURE_KEY);
    this.scene.textures.remove(GLOW_KEY);
    if (this.ceilingShade) this.scene.textures.remove(CEILING_KEY);
  }
}
